import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

const userFormSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  phoneNumber: z.string().min(10, "Phone number must be at least 10 digits").max(15, "Phone number must be at most 15 digits"),
  role: z.enum(["advisor", "client"], { errorMap: () => ({ message: "Please select a role" }) }),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

const userEditFormSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  phoneNumber: z.string().min(10, "Phone number must be at least 10 digits").max(15, "Phone number must be at most 15 digits"),
  role: z.enum(["advisor", "client"], { errorMap: () => ({ message: "Please select a role" }) }),
  password: z.string().min(8, "Password must be at least 8 characters").optional().or(z.literal("")),
});

type UserFormData = z.infer<typeof userFormSchema>;
type UserEditFormData = z.infer<typeof userEditFormSchema>;

type ExistingUser = {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string | null;
  role: string;
};

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: ExistingUser | null;
}

export function UserFormDialog({ open, onOpenChange, user }: UserFormDialogProps) {
  const { toast } = useToast();
  const isEditMode = !!user;

  const form = useForm<UserFormData | UserEditFormData>({
    resolver: zodResolver(isEditMode ? userEditFormSchema : userFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phoneNumber: "",
      role: "client",
      password: "",
    },
  });

  const watchedRole = form.watch("role");

  // Reset form when user data changes or dialog opens
  useEffect(() => {
    if (open) {
      if (user) {
        // Edit mode: populate with existing user data
        form.reset({
          fullName: user.fullName || "",
          email: user.email || "",
          phoneNumber: user.phoneNumber || "",
          role: (user.role as "advisor" | "client") || "client",
          password: "",
        });
      } else {
        // Create mode: reset to empty
        form.reset({
          fullName: "",
          email: "",
          phoneNumber: "",
          role: "client",
          password: "",
        });
      }
    }
  }, [open, user, form]);

  const createMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      return await apiRequest("POST", "/api/admin/users", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({
        title: "Success",
        description: "User created successfully",
      });
      onOpenChange(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create user",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UserEditFormData) => {
      // Don't send password if it's empty
      const updateData: any = {
        fullName: data.fullName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        role: data.role,
      };
      if (data.password && data.password.length > 0) {
        updateData.password = data.password;
      }
      return await apiRequest("PATCH", `/api/admin/users/${user!.id}`, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({
        title: "Success",
        description: "User updated successfully",
      });
      onOpenChange(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user",
        variant: "destructive",
      });
    },
  });

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): create mints a new user row per fire. Update is last-wins,
  // but both share the one submit button, so both route through the same lock.
  const submitFlight = useSingleFlight();

  const onSubmit = (data: UserFormData | UserEditFormData) => {
    if (isEditMode) {
      submitFlight.runQuiet(() => updateMutation.mutateAsync(data as UserEditFormData));
    } else {
      submitFlight.runQuiet(() => createMutation.mutateAsync(data as UserFormData));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="dialog-user-form">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit User" : "Add New User"}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update user information. Leave password blank to keep current password."
              : "Create a new advisor or client account."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input {...field} data-testid="input-fullName" placeholder="John Doe" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" data-testid="input-email" placeholder="john@example.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input {...field} data-testid="input-phoneNumber" placeholder="1234567890" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} data-testid="select-role">
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="client" data-testid="option-client">Client</SelectItem>
                      <SelectItem value="advisor" data-testid="option-advisor">Advisor</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{isEditMode ? "New Password (optional)" : "Password"}</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" data-testid="input-password" placeholder={isEditMode ? "Leave blank to keep current" : "••••••••"} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                data-testid="button-submit"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : isEditMode
                  ? "Update User"
                  : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
